import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { Badge } from '@/components/ui/Badge';
import { useSystemLookups } from '@/features/settings/hooks/useSystemLookups';
import { useStaffUsers } from '@/features/contacts/hooks/useStaffUsers';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface JobSearchResult {
  id: string;
  title: string;
  jobCode: string | null;
  jobType: string;
  status: string;
  priority: string;
  clientName: string;
  clientId: string;
  practiceEntityId: string | null;
  assignedAccountantName: string | null;
  dueDate: string | null;
}

export interface JobSearchModalProps {
  /** Called when a job is selected */
  onSelect: (job: JobSearchResult) => void;
  /** Called when the modal is closed without selection */
  onClose: () => void;
  /** Pre-filter jobs by this client ID if provided */
  clientId?: string;
  /** Modal title override */
  title?: string;
  /** Default status filter (defaults to 'in_progress') */
  defaultStatus?: string;
  /** Statuses to exclude from the filter dropdown and results */
  excludeStatuses?: string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  waiting_on_client: 'Waiting on Client',
  in_review: 'In Review',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Shared modal for searching and selecting a job.
 *
 * Features:
 * - Full-text search by title, job code, or type
 * - Filters: status, job type, client, assigned role/user
 * - Paginated results
 * - Accessible modal with keyboard support
 *
 * Used across: invoice creation, time entry recording, resource planner, WIP adjustments.
 */
export function JobSearchModal({
  onSelect,
  onClose,
  clientId,
  title = 'Search & Select Job',
  defaultStatus = 'in_progress',
  excludeStatuses = [],
}: JobSearchModalProps) {
  const { data: jobTypes } = useSystemLookups('job_types');
  const { data: staffUsers } = useStaffUsers();

  // Fetch clients for the client filter dropdown
  const { data: clientsData } = useQuery({
    queryKey: ['job-search-modal-clients'],
    queryFn: async () => {
      const res = await apiClient.get<{
        success: boolean;
        data: Record<string, unknown>[];
      }>('/contacts?contact_nature=client&pageSize=200');
      return (res.data.data ?? []).map((c) => ({
        id: c.id as string,
        name:
          `${c.first_name || ''} ${c.last_name || ''}`.trim() ||
          (c.business_name as string) ||
          (c.email as string) ||
          '',
      }));
    },
    staleTime: 5 * 60_000,
  });

  // ─── Filter state ──────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(defaultStatus);
  const [jobType, setJobType] = useState('');
  const [clientFilter, setClientFilter] = useState(clientId || '');
  const [assignedRole, setAssignedRole] = useState<'' | 'partner' | 'manager' | 'accountant'>('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [page, setPage] = useState(1);

  // Keep client filter locked to the provided clientId prop
  useEffect(() => {
    if (clientId) {
      setClientFilter(clientId);
    }
  }, [clientId]);

  // Reset user when role changes
  useEffect(() => {
    setAssignedUserId('');
  }, [assignedRole]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, status, jobType, clientFilter, assignedRole, assignedUserId]);

  // ─── Jobs search query ─────────────────────────────────────────────────────
  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: [
      'job-search-modal',
      search,
      status,
      jobType,
      assignedRole,
      assignedUserId,
      clientFilter,
      page,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', '15');
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      if (jobType) params.set('job_type', jobType);
      if (clientFilter) params.set('client_id', clientFilter);
      if (assignedRole) params.set('assigned_role', assignedRole);
      if (assignedUserId) params.set('assigned_user_id', assignedUserId);

      const res = await apiClient.get<{
        success: boolean;
        data: Record<string, unknown>[];
        pagination: { page: number; pageSize: number; total: number };
      }>(`/jobs?${params.toString()}`);

      const items: JobSearchResult[] = (res.data.data ?? [])
        .map((j) => ({
          id: j.id as string,
          title: (j.title || '') as string,
          jobCode: (j.job_code || null) as string | null,
          jobType: (j.job_type || '') as string,
          status: (j.status || '') as string,
          priority: (j.priority || '') as string,
          clientName: (j.client_name || '') as string,
          clientId: (j.client_id || '') as string,
          practiceEntityId: (j.practice_entity_id || null) as string | null,
          assignedAccountantName: (j.assigned_accountant_name || null) as string | null,
          dueDate: (j.due_date || null) as string | null,
        }))
        .filter((j) => !excludeStatuses.includes(j.status));

      return { items, pagination: res.data.pagination };
    },
    staleTime: 30_000,
  });

  const handleJobClick = useCallback(
    (job: JobSearchResult) => {
      onSelect(job);
    },
    [onSelect],
  );

  const jobs = jobsData?.items ?? [];
  const pagination = jobsData?.pagination;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="job-search-modal-title"
    >
      <div className="flex h-[85vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-2xl dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2
            id="job-search-modal-title"
            className="text-lg font-semibold text-gray-900 dark:text-gray-100"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Filters */}
          <div className="border-b border-gray-200 bg-gray-50 px-6 py-3 dark:border-gray-700 dark:bg-gray-800/50">
            <div className="mb-3">
              <div className="relative">
                <svg
                  className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by title, job code, or type..."
                  className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {/* Status */}
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                aria-label="Filter by status"
              >
                <option value="">All Statuses</option>
                {!excludeStatuses.includes('not_started') && (
                  <option value="not_started">Not Started</option>
                )}
                {!excludeStatuses.includes('in_progress') && (
                  <option value="in_progress">In Progress</option>
                )}
                {!excludeStatuses.includes('on_hold') && <option value="on_hold">On Hold</option>}
                {!excludeStatuses.includes('waiting_on_client') && (
                  <option value="waiting_on_client">Waiting on Client</option>
                )}
                {!excludeStatuses.includes('in_review') && (
                  <option value="in_review">In Review</option>
                )}
                {!excludeStatuses.includes('complete') && (
                  <option value="complete">Complete</option>
                )}
              </select>

              {/* Job Type */}
              <select
                value={jobType}
                onChange={(e) => setJobType(e.target.value)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                aria-label="Filter by job type"
              >
                <option value="">All Types</option>
                {(jobTypes ?? [])
                  .filter((jt) => jt.isActive)
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((jt) => (
                    <option key={jt.code} value={jt.code}>
                      {jt.label}
                    </option>
                  ))}
              </select>

              {/* Client */}
              <select
                value={clientFilter}
                onChange={(e) => setClientFilter(e.target.value)}
                disabled={!!clientId}
                className={`rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 ${clientId ? 'cursor-not-allowed opacity-60' : ''}`}
                aria-label="Filter by client"
              >
                <option value="">All Clients</option>
                {(clientsData ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>

              {/* Assigned Role */}
              <select
                value={assignedRole}
                onChange={(e) => setAssignedRole(e.target.value as typeof assignedRole)}
                className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                aria-label="Filter by assigned role"
              >
                <option value="">All Roles</option>
                <option value="partner">Partner</option>
                <option value="manager">Manager</option>
                <option value="accountant">Accountant</option>
              </select>

              {/* Assigned Person — only shown when a role is selected */}
              {assignedRole && (
                <select
                  value={assignedUserId}
                  onChange={(e) => setAssignedUserId(e.target.value)}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
                  aria-label="Filter by assigned person"
                >
                  <option value="">Anyone</option>
                  {(staffUsers ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.email}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto">
            {jobsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
                <span className="ml-2 text-sm text-gray-500">Searching jobs...</span>
              </div>
            ) : jobs.length === 0 ? (
              <p className="py-12 text-center text-sm text-gray-500">
                No jobs found. Try adjusting your filters.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {jobs.map((job) => (
                  <li key={job.id}>
                    <button
                      type="button"
                      onClick={() => handleJobClick(job)}
                      className="flex w-full items-start gap-4 px-6 py-3.5 text-left transition-colors hover:bg-brand-50 dark:hover:bg-brand-900/10"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="truncate font-medium text-gray-900 dark:text-gray-100">
                            {job.title}
                          </span>
                          {job.jobCode && (
                            <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                              {job.jobCode}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>{job.clientName}</span>
                          {job.jobType && (
                            <>
                              <span>·</span>
                              <span>{job.jobType}</span>
                            </>
                          )}
                          {job.assignedAccountantName && (
                            <>
                              <span>·</span>
                              <span>{job.assignedAccountantName}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Badge
                          variant={
                            job.status === 'complete'
                              ? 'success'
                              : job.status === 'cancelled'
                                ? 'danger'
                                : job.status === 'in_progress'
                                  ? 'info'
                                  : 'default'
                          }
                          size="sm"
                        >
                          {STATUS_LABELS[job.status] ?? job.status}
                        </Badge>
                        <svg
                          className="h-4 w-4 text-gray-400"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Pagination */}
          {pagination && pagination.total > 15 && (
            <div className="flex items-center justify-between border-t border-gray-200 px-6 py-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <span>{pagination.total} jobs found</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="rounded border border-gray-300 px-2 py-1 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-800"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page * 15 >= pagination.total}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded border border-gray-300 px-2 py-1 hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-800"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
